import type { TenantTrialStatus } from "@harmonyso/tenants-sdk";
import * as z from "zod";

// Trial dates are calendar days. The SDK turns "YYYY-MM-DD" into a Date at UTC
// midnight, and the API accepts a Date only at UTC midnight, so every
// conversion here stays in UTC; local time would shift the day west of UTC.

export function toDateInputValue(date?: Date | null): string {
  return date ? date.toISOString().slice(0, 10) : "";
}

export function fromDateInputValue(value: string): Date | null {
  return value ? new Date(`${value}T00:00:00.000Z`) : null;
}

// Form fields hold "YYYY-MM-DD" strings, so comparing them as strings orders
// them as dates.
export const trialDateFormFields = {
  trial_start_date: z.string(),
  trial_end_date: z.string(),
};

export function checkTrialDates(
  values: { trial_start_date: string; trial_end_date: string },
  ctx: z.RefinementCtx,
): void {
  const { trial_start_date: start, trial_end_date: end } = values;
  if (!start !== !end) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Set both trial dates, or leave both empty",
      path: ["trial_end_date"],
    });
  } else if (start && end && start > end) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "The trial end date must not be before the start date",
      path: ["trial_end_date"],
    });
  }
}

export function formatTrialDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function describeTrial(trial?: TenantTrialStatus | null): string {
  if (!trial) {
    return "No trial";
  }
  switch (trial.state) {
    case "scheduled":
      return `Starts ${formatTrialDate(trial.start_date)}`;
    case "active":
      if (trial.days_left === 0) {
        return "Active, ends today";
      }
      return `Active, ${trial.days_left} ${trial.days_left === 1 ? "day" : "days"} left`;
    case "expired":
      return `Ended ${formatTrialDate(trial.end_date)}`;
  }
}

export type TrialTone = "green" | "yellow" | "red" | "grey";

export interface TrialIndicator {
  tone: TrialTone;
  label: string;
  period?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function activeIndicator(
  totalDays: number,
  daysLeft: number,
  period: string,
): TrialIndicator {
  // The percentage counts today as a remaining day; daysLeft counts like the
  // server's days_left, which is 0 on the last day.
  const percentLeft = Math.round(((daysLeft + 1) / totalDays) * 100);
  const tone: TrialTone =
    percentLeft > 50 ? "green" : percentLeft > 20 ? "yellow" : "red";
  const left =
    daysLeft === 0
      ? "Ends today"
      : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`;

  return { tone, label: `${left} (${percentLeft}% remaining)`, period };
}

function trialPeriod(start: Date, end: Date): string {
  return `${formatTrialDate(start)} → ${formatTrialDate(end)}`;
}

// Estimated in UTC from the dates alone, for lists that have no server state;
// near midnight it can differ from the state in the tenant's timezone.
export function getTrialIndicator(
  start: Date | null | undefined,
  end: Date | null | undefined,
  now: Date = new Date(),
): TrialIndicator {
  if (!start || !end) {
    return { tone: "green", label: "No trial" };
  }

  const period = trialPeriod(start, end);
  const today = utcDay(now);
  const first = utcDay(start);
  const last = utcDay(end);

  if (today < first) {
    return { tone: "grey", label: `Starts ${formatTrialDate(start)}`, period };
  }
  if (today > last) {
    return { tone: "red", label: `Ended ${formatTrialDate(end)}`, period };
  }
  return activeIndicator(
    (last - first) / DAY_MS + 1,
    (last - today) / DAY_MS,
    period,
  );
}

// From the server's trial state, which uses the tenant's timezone.
export function getTrialIndicatorFromStatus(
  trial?: TenantTrialStatus | null,
): TrialIndicator {
  if (!trial) {
    return { tone: "green", label: "No trial" };
  }

  const period = trialPeriod(trial.start_date, trial.end_date);
  switch (trial.state) {
    case "scheduled":
      return {
        tone: "grey",
        label: `Starts ${formatTrialDate(trial.start_date)}`,
        period,
      };
    case "expired":
      return {
        tone: "red",
        label: `Ended ${formatTrialDate(trial.end_date)}`,
        period,
      };
    case "active":
      return activeIndicator(
        (utcDay(trial.end_date) - utcDay(trial.start_date)) / DAY_MS + 1,
        trial.days_left,
        period,
      );
  }
}
